import { type FormEvent, useState } from 'react';
import { Link } from 'react-router';
import { type ApiKey, api, type Domain } from '../api';
import { KeyIcon, PlusIcon } from '../components/icons';
import {
  CodeButton,
  CopyButton,
  Dialog,
  Empty,
  ErrorNote,
  formatDate,
  PageHeader,
  SkeletonRows,
  timeAgo,
  useToast,
} from '../components/ui';
import { useLoad } from '../hooks';
import { useSession } from '../session';

export function ApiKeys() {
  const { team } = useSession();
  const canEdit = team.role !== 'member';
  const keys = useLoad(() => api<{ data: ApiKey[] }>('GET', '/api-keys'), []);
  const domains = useLoad(() => api<{ data: Domain[] }>('GET', '/domains'), []);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<(ApiKey & { token: string }) | null>(null);
  const [revoking, setRevoking] = useState<ApiKey | null>(null);
  const toast = useToast();
  const list = keys.data?.data ?? [];
  const domainName = (id: string) => domains.data?.data.find((d) => d.id === id)?.name;
  // Only once domains have loaded, so the notice doesn't flash for teams that have one.
  const noVerifiedDomain =
    domains.data !== undefined && !domains.data.data.some((d) => d.status === 'verified');

  return (
    <>
      <PageHeader
        title="API keys"
        description="Keys authenticate your servers. Use one per app or environment."
        actions={
          <>
            {canEdit ? (
              <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
                <PlusIcon />
                Create key
              </button>
            ) : null}
            <CodeButton
              title="Use your API key"
              snippets={[
                {
                  label: 'Node.js',
                  code: "import { Vaartix } from 'vaartix';\n\n// Reads VAARTIX_API_KEY from the environment.\nconst vaartix = new Vaartix();",
                },
                {
                  label: 'cURL',
                  code: 'curl https://api.vaartix.com/emails \\\n  -H "Authorization: Bearer $VAARTIX_API_KEY"',
                },
              ]}
            />
          </>
        }
      />
      <ErrorNote error={keys.error} />
      {noVerifiedDomain ? (
        <div className="banner">
          <span>
            <strong>You haven't verified a domain yet.</strong> Keys work right away for test
            emails: send from any address @test.vaartix.com to delivered@, bounced@ or
            complained@test.vaartix.com. To send real email,{' '}
            <Link to="/domains/new" className="link">
              verify a domain
            </Link>
            .
          </span>
        </div>
      ) : null}
      <div className="card">
        {!keys.loading && list.length === 0 ? (
          <Empty icon={<KeyIcon />} title="No API keys yet">
            <p>
              Create a key to send email from your app. You'll see it once, so copy it somewhere
              safe.
            </p>
            {canEdit ? (
              <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
                <PlusIcon />
                Create key
              </button>
            ) : null}
          </Empty>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Key</th>
                  <th>Access</th>
                  <th className="nowrap hide-sm">Last used</th>
                  <th className="nowrap hide-sm">Expires</th>
                  <th className="nowrap hide-sm">Created</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {keys.loading ? (
                  <SkeletonRows cols={7} rows={3} />
                ) : (
                  list.map((key) => (
                    <tr key={key.id}>
                      <td>
                        <div className="cell-main">
                          <span className={`row-icon ${key.scope === 'full' ? '' : 'ok'}`}>
                            <KeyIcon />
                          </span>
                          <strong>{key.name}</strong>
                        </div>
                      </td>
                      <td>
                        <code style={{ color: 'var(--muted)' }}>{key.prefix}…</code>
                      </td>
                      <td>
                        <span className={`status ${key.scope === 'full' ? 'accent' : ''}`}>
                          {key.scope === 'full' ? 'Full access' : 'Sending only'}
                        </span>
                        {key.domainRestricted ? (
                          <span className="sub">{domainSummary(key.domainIds, domainName)}</span>
                        ) : null}
                      </td>
                      <td className="nowrap hide-sm" style={{ color: 'var(--muted)' }}>
                        {key.lastUsedAt ? timeAgo(key.lastUsedAt) : 'Never'}
                      </td>
                      <td className="nowrap hide-sm" style={{ color: 'var(--muted)' }}>
                        {isExpired(key) ? (
                          <span className="status warn">Expired</span>
                        ) : key.expiresAt ? (
                          formatDate(key.expiresAt)
                        ) : (
                          'Never'
                        )}
                      </td>
                      <td className="nowrap hide-sm" style={{ color: 'var(--muted)' }}>
                        {formatDate(key.createdAt)}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {canEdit ? (
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            onClick={() => setRevoking(key)}
                          >
                            Revoke
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="page-note">
        A key isn't tied to a sender. You choose the sender in each email's <code>from</code>: any
        address on one of your verified domains, or on test.vaartix.com. To restrict a key, limit it
        to some of your domains or give it an expiry date when you create it.
      </p>

      <SmtpSettings />

      {creating ? (
        <CreateKey
          domains={domains.data?.data ?? []}
          onClose={() => setCreating(false)}
          onCreated={(key) => {
            setCreating(false);
            setCreated(key);
            keys.reload();
          }}
        />
      ) : null}

      {created ? (
        <Dialog
          title="Copy your API key"
          description="This is the only time it's shown. Store it in your server's environment, for example as VAARTIX_API_KEY."
          onClose={() => setCreated(null)}
          footer={
            <button type="button" className="btn btn-primary" onClick={() => setCreated(null)}>
              I've saved it
            </button>
          }
        >
          <div className="secret-box">
            <code>{created.token}</code>
            <CopyButton value={created.token} label="Copy key" />
          </div>
          {noVerifiedDomain ? (
            <p className="muted" style={{ margin: 0, fontSize: 13 }}>
              This key can only send test emails until you verify a domain.
            </p>
          ) : null}
        </Dialog>
      ) : null}

      {revoking ? (
        <Dialog
          title={`Revoke "${revoking.name}"?`}
          description="Apps using this key stop working immediately. This can't be undone."
          onClose={() => setRevoking(null)}
          footer={
            <>
              <button type="button" className="btn btn-ghost" onClick={() => setRevoking(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={async () => {
                  const key = revoking;
                  setRevoking(null);
                  try {
                    await api('DELETE', `/api-keys/${encodeURIComponent(key.id)}`);
                    toast.show('Key revoked');
                    keys.reload();
                  } catch (err) {
                    toast.show(err instanceof Error ? err.message : 'Could not revoke');
                  }
                }}
              >
                Revoke key
              </button>
            </>
          }
        />
      ) : null}
      {toast.node}
    </>
  );
}

function CreateKey({
  domains,
  onClose,
  onCreated,
}: {
  domains: Domain[];
  onClose: () => void;
  onCreated: (key: ApiKey & { token: string }) => void;
}) {
  const [name, setName] = useState('');
  const [scope, setScope] = useState<'send' | 'full'>('send');
  const [domainIds, setDomainIds] = useState<string[]>([]);
  const [expiry, setExpiry] = useState<string>('never');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function submit(e?: FormEvent) {
    e?.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onCreated(
        await api<ApiKey & { token: string }>('POST', '/api-keys', {
          name: name.trim(),
          scope,
          domainIds,
          expiresAt: expiryDate(expiry),
        }),
      );
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  }

  return (
    <Dialog
      title="Create an API key"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            form="create-key"
            className="btn btn-primary"
            disabled={busy || !name.trim()}
          >
            {busy ? 'Creating…' : 'Create key'}
          </button>
        </>
      }
    >
      <form id="create-key" onSubmit={submit} style={{ display: 'contents' }}>
        <div className="field">
          <label htmlFor="key-name">Name</label>
          <input
            id="key-name"
            className="input"
            maxLength={60}
            placeholder="Production server"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <fieldset className="field" style={{ border: 0, padding: 0, margin: 0 }}>
          <legend
            className="field"
            style={{ fontSize: 13, fontWeight: 500, color: '#d4d2cc', marginBottom: 6 }}
          >
            Access
          </legend>
          <div className="radio-cards">
            <label>
              <input
                type="radio"
                name="scope"
                checked={scope === 'send'}
                onChange={() => setScope('send')}
              />
              <strong>Sending only</strong>
              <span>Can send email. Best for app servers.</span>
            </label>
            <label>
              <input
                type="radio"
                name="scope"
                checked={scope === 'full'}
                onChange={() => setScope('full')}
              />
              <strong>Full access</strong>
              <span>Can also read emails and manage domains, keys and webhooks.</span>
            </label>
          </div>
        </fieldset>
        {domains.length ? (
          <div className="field">
            <div className="label">Limit to domains</div>
            <div className="checks-grid">
              {domains.map((d) => (
                <label key={d.id}>
                  <input
                    type="checkbox"
                    checked={domainIds.includes(d.id)}
                    onChange={() =>
                      setDomainIds((ids) =>
                        ids.includes(d.id) ? ids.filter((id) => id !== d.id) : [...ids, d.id],
                      )
                    }
                  />
                  <span>{d.name}</span>
                </label>
              ))}
            </div>
            <span className="hint">
              {domainIds.length
                ? 'The key can only send from the domains you ticked.'
                : 'None ticked: the key can send from any of your domains.'}
            </span>
          </div>
        ) : null}
        <div className="field">
          <label htmlFor="key-expiry">Expires</label>
          <select
            id="key-expiry"
            className="input"
            value={expiry}
            onChange={(e) => setExpiry(e.target.value)}
          >
            <option value="never">Never</option>
            <option value="30">In 30 days</option>
            <option value="90">In 90 days</option>
            <option value="365">In 1 year</option>
          </select>
          <span className="hint">
            An expired key stops working; create a new one to replace it.
          </span>
        </div>
        <ErrorNote error={error} />
      </form>
    </Dialog>
  );
}

const SMTP_SETTINGS: {
  label: string;
  value: string;
  copy?: boolean;
  /** A description rather than a value to type. */
  plain?: boolean;
  note?: string;
}[] = [
  { label: 'Host', value: 'smtp.vaartix.com', copy: true },
  { label: 'Port', value: '465', note: 'SSL/TLS. Or 587 with STARTTLS.' },
  { label: 'Username', value: 'vaartix', copy: true },
  {
    label: 'Password',
    value: 'One of your API keys',
    plain: true,
    note: 'A sending-only key made for that app is best.',
  },
];

/** For apps that can only send over SMTP, such as WordPress. */
function SmtpSettings() {
  return (
    <div className="card smtp-card">
      <div className="card-head">
        <div>
          <h2>SMTP</h2>
          <p>
            For WordPress and other apps that send over SMTP. Encryption and login are required.
          </p>
        </div>
        <a
          className="btn btn-ghost btn-sm"
          href="https://vaartix.com/docs/sending/smtp/"
          target="_blank"
          rel="noreferrer"
        >
          Setup guides
        </a>
      </div>
      <div className="table-wrap">
        <table className="table">
          <tbody>
            {SMTP_SETTINGS.map((row) => (
              <tr key={row.label}>
                <td className="nowrap dim" style={{ width: 120 }}>
                  {row.label}
                </td>
                <td>
                  {row.copy ? (
                    <span className="copyable">
                      <code>{row.value}</code>
                      <CopyButton value={row.value} label={`Copy ${row.label.toLowerCase()}`} />
                    </span>
                  ) : row.plain ? (
                    <span>{row.value}</span>
                  ) : (
                    <code>{row.value}</code>
                  )}
                  {row.note ? <span className="sub">{row.note}</span> : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** The expiry chosen in the create dialog as an ISO date, or null for never. */
function expiryDate(choice: string): string | null {
  const days = Number(choice);
  return Number.isFinite(days) ? new Date(Date.now() + days * 86_400_000).toISOString() : null;
}

function isExpired(key: ApiKey, now = Date.now()): boolean {
  return key.expiresAt !== null && new Date(key.expiresAt).getTime() <= now;
}

/** "acme.in", "acme.in +2" or "No domains" for a restricted key. */
function domainSummary(ids: string[], name: (id: string) => string | undefined): string {
  if (ids.length === 0) return 'No domains';
  const first = name(ids[0] as string) ?? '1 domain';
  return ids.length === 1 ? first : `${first} +${ids.length - 1}`;
}
